import React from 'react';
import { Box, Button, Icon, StatusIndicator, TreeView, TreeViewProps } from '@amzn/awsui-components-core';
import { getReadableDateFromDateTime } from 'src/utilities/DateTimeUtilities';
import { FolderNodeState } from '../../hooks/useWorkDocsFolderBrowser';
import { formatFileSize } from '../../utils/formatFileSize';
import { FIXED_SUBFOLDERS, GENERATED_CARDS_FOLDER, SUPPLEMENTAL_DATA_FOLDER } from '../../utils/workDocsFolderPlan';

/**
 * The fixed folders shown at the ROOT level of the tree: every provisioned fixed folder except
 * the hidden PDF output folder (never an upload target). Derived from FIXED_SUBFOLDERS so the tree
 * tracks the single source of truth — if that set changes, the browsable root folders follow.
 * A Set for O(1) membership. Applies only at root: per-person folders inside Supplemental Context
 * are named by login (not in FIXED_SUBFOLDERS) and are filtered by `selectedAliases` instead.
 */
const ROOT_UPLOAD_FOLDERS: ReadonlySet<string> = new Set(FIXED_SUBFOLDERS.filter((name) => name !== GENERATED_CARDS_FOLDER));

/**
 * A node in the WorkDocs folder tree. Folders are expandable and carry an upload action;
 * files are leaves showing size/date. `synthetic` nodes are non-interactive placeholders
 * (e.g. "Loading…" / "Empty") shown as the sole child of a folder with no real children yet.
 */
export type FolderTreeNode =
  | { kind: 'folder'; id: string; name: string; inSupplementalSubtree: boolean }
  | { kind: 'file'; id: string; name: string; size: number; modified: string; parentFolderId: string; documentId: string }
  | { kind: 'synthetic'; id: string; label: string };

export interface WorkDocsFolderTreeProps {
  /** The root folder id to browse from, or null when folders aren't provisioned. */
  rootFolderId: string | null;
  /** Display name for the root node (e.g. `{alias}-baseball-cards`). */
  rootFolderName: string;
  expandedIds: Set<string>;
  nodeStateById: Map<string, FolderNodeState>;
  onExpandFolder: (folderId: string) => void;
  onCollapseFolder: (folderId: string) => void;
  /** Opens the upload modal targeting the given folder id. */
  onUploadToFolder: (folderId: string) => void;
  /** Requests deletion of one uploaded document — opens a confirmation for the given file. */
  onDeleteFile: (parentFolderId: string, documentId: string) => void;
  /**
   * Logins selected in Step 2. Inside the "Supplemental Context" subtree (whose per-person
   * subfolders are named by login), only folders for a selected login are shown; the rest of the
   * tree (root, fixed subfolders, their files) is unaffected.
   */
  selectedAliases: ReadonlySet<string>;
}

// File-node ids are prefixed so they never collide with a folder id in the same tree.
const FILE_ID_PREFIX = 'file:';
const SYNTH_ID_PREFIX = 'synthetic:';

/**
 * Builds the children of one folder from the browser hook's cached listing: its subfolders
 * (as folder nodes) followed by its documents (as file nodes). Returns `undefined` for
 * non-folder nodes (leaves). A folder that hasn't loaded yet (or is mid-load / empty) gets a
 * single synthetic placeholder so it still renders as expandable and shows feedback.
 *
 * Within the "Supplemental Context" subtree (per-person folders named by login, nested to mirror
 * the manager→reports hierarchy), only folders for a selected login are shown. A child is "in the
 * supplemental subtree" when its parent IS that folder or is already in the subtree, so the filter
 * applies at every depth — an unselected report nested under a selected manager is dropped too.
 */
const getChildrenFor = (
  node: FolderTreeNode,
  nodeStateById: Map<string, FolderNodeState>,
  selectedAliases: ReadonlySet<string>
): ReadonlyArray<FolderTreeNode> | undefined => {
  if (node.kind !== 'folder') return undefined;

  const state = nodeStateById.get(node.id);
  if (!state || !state.loaded) {
    return [{ kind: 'synthetic', id: `${SYNTH_ID_PREFIX}${node.id}`, label: state?.isLoading ? 'Loading…' : 'Expand to load' }];
  }

  // Children of this folder sit in the supplemental subtree if this folder is the Supplemental
  // Context container itself, or is already inside that subtree.
  const childrenInSupplementalSubtree = node.inSupplementalSubtree || node.name === SUPPLEMENTAL_DATA_FOLDER;

  // Which folders are browsable depends on the zone:
  // - Root level: show ONLY the fixed upload folders (ROOT_UPLOAD_FOLDERS, derived from
  //   FIXED_SUBFOLDERS minus the hidden output folder). This hides the PDF output folder AND any
  //   leftover folders from a pre-change setup (e.g. old "Annual Review Feedback" / "Prior Cycle
  //   Baseball Cards" roots) that are no longer part of the flow.
  // - Inside the Supplemental Context subtree: show person folders (named by login) only for a
  //   login selected in Step 2. These names are NOT in ROOT_UPLOAD_FOLDERS, so the allowlist must
  //   not apply here — the two filters are mutually exclusive by `childrenInSupplementalSubtree`.
  // Filtering here (not in renderItem) keeps hidden folders out of the child count too, so the
  // "Empty Folder" check below reflects only browsable folders.
  const folderChildren: FolderTreeNode[] = state.folders
    .filter((folder) => (childrenInSupplementalSubtree ? selectedAliases.has(folder.name) : ROOT_UPLOAD_FOLDERS.has(folder.name)))
    .map((folder) => ({ kind: 'folder', id: folder.id, name: folder.name, inSupplementalSubtree: childrenInSupplementalSubtree }));
  const fileChildren: FolderTreeNode[] = state.documents.map((doc) => ({
    kind: 'file',
    id: `${FILE_ID_PREFIX}${doc.id}`,
    name: doc.name,
    size: doc.size,
    modified: doc.modifiedTimestamp,
    // `node` is the parent folder here; carry both so the delete action has the raw document id
    // (the node `id` above is prefixed) and the folder to refresh after a successful delete.
    parentFolderId: node.id,
    documentId: doc.id
  }));

  const children = [...folderChildren, ...fileChildren];
  if (children.length === 0) {
    return [{ kind: 'synthetic', id: `${SYNTH_ID_PREFIX}${node.id}`, label: 'Empty Folder' }];
  }
  return children;
};

/**
 * Browses the provisioned WorkDocs folder tree as a Cloudscape TreeView with connector lines.
 * Folders lazy-load their contents on expand (via `onExpandFolder`, which the browser hook
 * turns into a list call) and carry an inline Upload action; files are leaves showing size and
 * last-modified date. Renders a guidance message until a root folder id exists.
 */
export const WorkDocsFolderTree: React.FC<WorkDocsFolderTreeProps> = ({
  rootFolderId,
  rootFolderName,
  expandedIds,
  nodeStateById,
  onExpandFolder,
  onCollapseFolder,
  onUploadToFolder,
  onDeleteFile,
  selectedAliases
}) => {
  if (!rootFolderId) {
    return <StatusIndicator type="warning">Set up WorkDocs folders in the previous step to browse and upload documents.</StatusIndicator>;
  }

  const rootNode: FolderTreeNode = { kind: 'folder', id: rootFolderId, name: rootFolderName, inSupplementalSubtree: false };

  const renderItem = (item: FolderTreeNode): TreeViewProps.TreeItem => {
    if (item.kind === 'file') {
      return {
        content: item.name,
        announcementLabel: item.name,
        icon: <Icon name="file" />,
        secondaryContent: `${formatFileSize(item.size)}${item.modified ? ` · ${getReadableDateFromDateTime(item.modified)}` : ''}`,
        actions: (
          <Button
            variant="inline-icon"
            iconName="remove"
            ariaLabel={`Delete ${item.name}`}
            onClick={() => onDeleteFile(item.parentFolderId, item.documentId)}
          />
        )
      };
    }

    if (item.kind === 'synthetic') {
      return {
        content: (
          <Box color="text-body-secondary" fontSize="body-s">
            {item.label}
          </Box>
        ),
        announcementLabel: item.label
      };
    }

    // Folder node: the NAME is the upload trigger for real targets. The root and the
    // "Supplemental Context" container hold documents only in their subfolders, so their names
    // are plain (non-clickable) text — still expandable via the tree's own chevron. Any load
    // error shows inline. (rootFolderId is non-null here: renderItem runs after the guard above.)
    const error = nodeStateById.get(item.id)?.error;
    const isUploadTarget = item.id !== rootFolderId && item.name !== SUPPLEMENTAL_DATA_FOLDER;
    return {
      content: isUploadTarget ? (
        <Button variant="inline-link" ariaLabel={`Upload to ${item.name}`} onClick={() => onUploadToFolder(item.id)}>
          {item.name}
        </Button>
      ) : (
        item.name
      ),
      announcementLabel: item.name,
      icon: <Icon name={expandedIds.has(item.id) ? 'folder-open' : 'folder'} />,
      secondaryContent: error ? <StatusIndicator type="error">{error}</StatusIndicator> : undefined
    };
  };

  return (
    <TreeView<FolderTreeNode>
      items={[rootNode]}
      getItemId={(item) => item.id}
      renderItem={renderItem}
      getItemChildren={(item) => getChildrenFor(item, nodeStateById, selectedAliases)}
      expandedItems={Array.from(expandedIds)}
      onItemToggle={({ detail }) => {
        // Only folder nodes are expandable; synthetic/file ids never reach here as toggles.
        if (detail.item.kind !== 'folder') return;
        if (detail.expanded) {
          onExpandFolder(detail.item.id);
        } else {
          onCollapseFolder(detail.item.id);
        }
      }}
      connectorLines="vertical"
      ariaLabel="WorkDocs Folders"
      i18nStrings={{
        expandButtonLabel: (item) => `Expand ${item.kind === 'folder' ? item.name : 'item'}`,
        collapseButtonLabel: (item) => `Collapse ${item.kind === 'folder' ? item.name : 'item'}`
      }}
    />
  );
};
